import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

/**
 * 首页组件面板的 widget 注册表：这里登记 widget **类型**（元数据
 * + 组件），每块卡片的实际布局（顺序 / 尺寸 / 是否上板）由
 * widgets/layout.ts 管理、持久在 localStorage。新增 widget 在此
 * 登记即可，已保存过布局的面板会自动把新类型补到末尾。
 */

/** widget 尺寸档位——iOS 桌面小组件式的固定三档，不做逐维度调宽高。 */
export type WidgetSize = 'sm' | 'md' | 'lg';

/** 档位 → 栅格占位（列跨度 × 8rem 行数），宽高节奏对齐 iPhone
 * 小组件：小 ≈ 一格方块（1×2），中 ≈ 两格宽（2×2），大 = 中的双倍
 * 高（2×4）。 */
export const SIZE_GRID: Record<WidgetSize, { span: 1 | 2; rows: 2 | 4 }> = {
  sm: { span: 1, rows: 2 },
  md: { span: 2, rows: 2 },
  lg: { span: 2, rows: 4 },
};

/** 档位中文名（工具提示 / 无障碍标签用）。 */
export const SIZE_LABEL: Record<WidgetSize, string> = {
  sm: '小',
  md: '中',
  lg: '大',
};

/** 编辑态点按卡片循环切换：小 → 中 → 大 → 小。 */
export function nextSize(size: WidgetSize): WidgetSize {
  const order: WidgetSize[] = ['sm', 'md', 'lg'];
  return order[(order.indexOf(size) + 1) % order.length];
}

export interface WidgetDef {
  key: string;
  title: string;
  description: string;
  /** 默认尺寸档位。 */
  size: WidgetSize;
  Component: LazyExoticComponent<ComponentType>;
}

export const widgets: WidgetDef[] = [
  {
    key: 'review',
    title: '背词',
    description: 'mini 背词会话，直接在面板背词',
    size: 'md',
    Component: lazy(() => import('./Review')),
  },
  {
    key: 'scratch',
    title: '白板',
    description: '随手记点什么，自动保存到 notebook',
    size: 'sm',
    Component: lazy(() => import('./Scratch')),
  },
  {
    key: 'leech',
    title: '顽固词',
    description: '背词中反复失败的词，提醒补助记',
    size: 'md',
    Component: lazy(() => import('./Leech')),
  },
];

/** 列跨度 → 类名（md 两列起 span 2 占满半行/整行；lg 三列下即「中」）。 */
export function spanClass(span: 1 | 2): string {
  return span >= 2 ? 'md:col-span-2' : '';
}

/** 行跨度 → row-span 类名（静态映射，Tailwind 需要扫到字面类名）。 */
const ROW_SPAN = [
  'row-span-1',
  'row-span-2',
  'row-span-3',
  'row-span-4',
] as const;

export function rowSpanClass(rows: number): string {
  const clamped = Math.min(4, Math.max(1, Math.round(rows)));
  return ROW_SPAN[clamped - 1];
}
